/**
 * The browser's session: the access token in memory, the refresh token where the browser keeps it.
 *
 * Two rules from the contract shape this file, and the second one is what most of the code is about.
 *
 * **The access token is never persisted.** It lives in a module variable, so a page reload starts with
 * nothing and the token is not readable by a script that outlives the tab. The refresh token is an
 * `httpOnly` cookie the browser attaches to `/api/v1` and JavaScript cannot read — which is why there
 * is no storage call anywhere in this file.
 *
 * **Refresh rotation is single-use, so concurrent refreshes are destructive.** The API treats a
 * second use of a spent refresh token as evidence of theft and revokes the *entire* rotation family —
 * every session the user has, on every device. In a browser that is not a theoretical risk: five
 * queries that all 401 together will each ask for a refresh, four of them will present the token the
 * first one just spent, and the user is signed out everywhere. `refreshInFlight` collapses them into
 * one request. This is the single most important thing in the file, and it is the reason
 * `onUnauthorized` returns a promise rather than doing the work directly.
 *
 * Boot is `restore()`, not "read the token": the token is gone after a reload by design, so the only
 * way to know whether the session survived is to ask for a new access token. A browser with no cookie
 * gets a 401, which is the ordinary signed-out state and not an error to show.
 *
 * **Public mode replaces that boot rather than adding a fallback to it.** When the server reports
 * `features.public_mode` in `/meta`, the deployment has no private accounts: every visitor is one shared
 * account, and the app must neither restore nor present a stored session. The mode is therefore read
 * *first*, and when it is on the boot never calls `refresh()` — a browser still holding the `trippool_rt`
 * refresh cookie from an earlier sign-in would otherwise come back as its own account with its own data,
 * in a phase whose whole point is that everyone shares one. That leftover credential is cleared through
 * the API's own `POST /auth/logout` before the shared account is read from `GET /me`, so the next load
 * starts from nothing rather than making the same decision again. With the flag off, `/meta` says so and
 * the boot is the refresh-then-sign-in it was before: a stored session is honoured exactly as it always
 * was.
 */

import { ApiError, createApiClient, type ApiClient } from "@trippool/api-client";
import type { AuthUser } from "@trippool/contracts";

/** Where the API lives. Same origin in every environment: the Vite dev server proxies to it. */
const API_BASE_URL = "/api/v1";

/**
 * What the deployment is, as far as this client knows.
 *
 * **Three states, and the third is the point.** A boolean cannot say "not asked yet", so a client that
 * has not heard from `/meta` answers "not public" — which reads as "private" and is how a hard load of a
 * non-home path painted an authentication form on a deployment that has no sign-in. `unknown` is the
 * state before `/meta` answers, and until it becomes one of the other two no screen may conclude anything
 * from it: an authentication form is a claim about the deployment, and one made before the answer has
 * arrived is a guess.
 */
export type PublicMode = "unknown" | "public" | "private";

export interface SessionState {
  /** The signed-in account, or null when signed out. */
  user: AuthUser | null;
  /**
   * Whether the initial `restore()` has finished.
   *
   * Distinct from `user === null`, and the distinction is the whole reason this field exists: before
   * restore completes, "no user" means "not known yet" and the app must not flash the sign-in screen
   * at someone who is about to be signed in.
   */
  restored: boolean;
  /**
   * The deployment's mode, which is decided before `restored` can become true.
   *
   * Part of the state rather than a second channel, because it decides what the boot may do next and
   * what the shell may render: one notification carries it, and a screen cannot be re-rendered with a
   * `restored` user and a stale mode. It never makes the app render as signed out — only an explicit
   * `private` does that, and only the server can produce one.
   */
  mode: PublicMode;
}

type Listener = (state: SessionState) => void;

export interface SessionManager {
  /** The client, already wired to the in-memory token and the refresh path. */
  client: ApiClient;
  getState(): SessionState;
  subscribe(listener: Listener): () => void;
  /**
   * Boot the session: resolve who this visit is, before any screen decides.
   *
   * Resolves to whether a user was established. In public mode that is the shared account and no
   * credential is presented; with the flag off it is a refresh-cookie session, if any.
   */
  restore(): Promise<boolean>;
  /**
   * What this deployment is, from the server's `/meta` — never inferred.
   *
   * `unknown` until `/meta` answers, and a screen must not read "not public" out of that: the mode is a
   * measurement, not a default. Screens use a decided answer to hide the affordances that would lie in
   * this phase — a sign-out button, a device list — rather than leaving them pretending.
   */
  getPublicMode(): PublicMode;
  signInAsGuest(): Promise<AuthUser>;
  signInWithPassword(email: string, password: string): Promise<AuthUser>;
  registerWithPassword(email: string, password: string, displayName: string): Promise<AuthUser>;
  claimWithPassword(email: string, password: string): Promise<AuthUser>;
  signOut(): Promise<void>;
  /** Called after the account changes on another screen, so the header updates without a refetch. */
  setUser(user: AuthUser | null): void;
}

export interface SessionManagerOptions {
  baseUrl?: string;
  /**
   * The fetch implementation.
   *
   * Injected so a test can drive the whole manager without a server, and so the cookie behaviour of a
   * real browser is preserved in production — `credentials: "same-origin"` is the default, and this
   * wrapper exists to make it explicit rather than accidental.
   */
  fetch?: typeof globalThis.fetch;
}

export function createSessionManager(options: SessionManagerOptions = {}): SessionManager {
  const baseUrl = options.baseUrl ?? API_BASE_URL;

  let accessToken: string | null = null;
  let state: SessionState = { user: null, restored: false, mode: "unknown" };
  const listeners = new Set<Listener>();

  function setState(next: Partial<SessionState>): void {
    state = { ...state, ...next };
    for (const listener of listeners) listener(state);
  }

  /**
   * The in-flight refresh, shared by every caller that asks while one is running.
   *
   * See the file comment: without this, N concurrent 401s produce N refreshes, and rotation makes all
   * but the first look like a stolen token — which revokes every session the user has. The failure is
   * self-inflicted and complete, and it would look to the user like being logged out at random.
   */
  let refreshInFlight: Promise<boolean> | null = null;

  const fetchImpl: typeof globalThis.fetch =
    options.fetch ??
    ((input, init) =>
      // `credentials: "same-origin"` is the default for a same-origin request, but stating it means a
      // later change to the API's location cannot silently stop the cookie being sent.
      globalThis.fetch(input, { ...init, credentials: "same-origin" }));

  /** Apply a session response: keep the token in memory and publish the account. */
  function adopt(session: { access_token: string; user: AuthUser }): AuthUser {
    accessToken = session.access_token;
    setState({ user: session.user, restored: true });
    return session.user;
  }

  async function refresh(): Promise<boolean> {
    if (refreshInFlight !== null) return refreshInFlight;

    refreshInFlight = (async () => {
      try {
        // No body: the cookie is the credential, and `refresh_token_transport` tells the native
        // client to read the value from the response instead.
        const session = await client.refreshSession();
        adopt(session);
        return true;
      } catch {
        /*
         * Any failure here means no session: no cookie, an expired one, or a detected reuse. The user
         * is signed out, which is a normal state with its own screen — not an error to surface. The
         * one case worth a different message is reuse, and the API's `detail` carries it; the app shows
         * that on the sign-in screen rather than here, because that is where the user ends up.
         */
        accessToken = null;
        setState({ user: null, restored: true });
        return false;
      } finally {
        // Cleared unconditionally, including on failure, so a transient network error does not make
        // every later refresh return the same stale rejection.
        refreshInFlight = null;
      }
    })();

    return refreshInFlight;
  }

  const client = createApiClient({
    baseUrl,
    fetch: fetchImpl,
    getToken: () => accessToken,
    /*
     * Called once by the client when a request comes back 401, after which it retries once. Returning
     * the shared promise is what makes concurrent 401s a single refresh rather than a stampede.
     */
    onUnauthorized: async () => {
      await refresh();
    },
  });

  /**
   * Ask the server which mode it is in, and publish the answer.
   *
   * `/meta` is unauthenticated and answers from configuration — no database, no provider — so it is the
   * one read that is safe before a session exists. Taken from the server rather than inferred from a
   * failed refresh: a signed-out visitor and a deployment with no sign-in produce the same 401, and only
   * the server can tell them apart.
   *
   * **An unreadable answer decides nothing.** The mode stays `unknown` rather than falling back to
   * `private`, because that fallback is the bug this state exists to remove: it would render an
   * authentication form for a deployment that may well have none. A server that cannot answer `/meta`
   * cannot serve the app either, so the loading state the unknown mode produces is not hiding anything
   * that would have loaded.
   */
  async function loadPublicMode(): Promise<PublicMode> {
    try {
      const meta = await client.getMeta();
      // A boolean from the server, made into one of the two decided states. `false` here is an answer,
      // not an absence — the difference between this and the `catch` below is the whole fix.
      const mode: PublicMode = meta.features.public_mode ? "public" : "private";
      if (mode !== state.mode) setState({ mode });
    } catch {
      // No answer: leave the mode unknown, and let the boot decline to decide.
    }
    return state.mode;
  }

  /**
   * Boot in public mode: one shared account, and no stored session.
   *
   * This is the fix for the one way a browser could escape the shared pool. A visitor who signed in
   * while the flag was off still holds the `trippool_rt` refresh cookie, and the boot must not spend it:
   * presenting it at `POST /auth/refresh` would return the visitor as their own private account, with
   * their own data, which is exactly the silo this deployment does not have. So the refresh is never
   * called; instead the leftover credential is cleared with the API's own sign-out, and the visitor is
   * read as the shared account from `GET /me`.
   *
   * The clear comes first and is best-effort. It is what keeps the app from carrying a live credential
   * it will never use — the browser would keep sending the cookie, and "cleared rather than left to
   * confuse". `POST /auth/logout` in public mode is answered by the shared account (there is no token to
   * carry), and `sessionId` is the all-zero sentinel that names no row, so its only effect here is the
   * server's `Max-Age=0` on the cookie. A failure — the API refusing, the network down — must not stop
   * the visitor reaching Home, so it is swallowed the same way a failed refresh is.
   */
  async function adoptSharedAccount(): Promise<boolean> {
    try {
      await client.logout();
    } catch {
      // The cookie may survive; the visitor is still served as the shared account below.
    }

    try {
      // Deliberately no token: in public mode the API resolves the caller itself.
      const me = await client.getMe();
      setState({ user: me.user, restored: true });
      return true;
    } catch {
      /*
       * Public mode is on but the shared account could not be read — a server started before its
       * migrations, most often. The API logs the reason at `warn`; here the honest client state is the
       * ordinary signed-out one, not a fabricated user. `restored` is still set, or the app would sit on
       * its loading line forever: unlike the flag-off boot, no refresh ran to settle it.
       */
      setState({ user: null, restored: true });
      return false;
    }
  }

  /**
   * Boot: establish what the deployment is, then who this visit is.
   *
   * The mode is read first, because it decides whether a stored session may be presented at all and
   * whether the app may show a sign-in screen. In public mode nothing is restored and the shared account
   * is adopted; in a *decided* private deployment the refresh runs, which is the only way to know whether
   * the cookie is still a session, and a null user after it is the ordinary signed-out state.
   *
   * **An undecided mode ends the boot without deciding.** No refresh is presented — a stored credential
   * must not be spent on a deployment that might be public, which is the silo this phase exists to close
   * — and `restored` is left false, so the shell keeps saying it is still asking instead of painting a
   * form. That is the whole difference from the old boolean, whose "not public" was read as "private".
   *
   * Single-flighted for the same reason `refresh` is: the several screens that mount at once must produce
   * one boot, not one shared-account read each.
   */
  let bootInFlight: Promise<boolean> | null = null;

  async function boot(): Promise<boolean> {
    if (bootInFlight !== null) return bootInFlight;

    bootInFlight = (async () => {
      try {
        const mode = await loadPublicMode();
        if (mode === "public") return adoptSharedAccount();
        if (mode === "private") return refresh();
        return false;
      } finally {
        bootInFlight = null;
      }
    })();

    return bootInFlight;
  }

  return {
    client,
    getState: () => state,

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    async restore() {
      return boot();
    },

    getPublicMode: () => state.mode,

    async signInAsGuest() {
      return adopt(await client.signInAsGuest());
    },

    async signInWithPassword(email, password) {
      return adopt(await client.loginWithPassword({ email, password }));
    },

    async registerWithPassword(email, password, displayName) {
      return adopt(await client.registerWithPassword({ email, password, display_name: displayName }));
    },

    async claimWithPassword(email, password) {
      return adopt(await client.claimGuestAccount({ method: "password", email, password }));
    },

    async signOut() {
      try {
        await client.logout();
      } catch (error) {
        /*
         * A failed logout must still sign the user out locally.
         *
         * Leaving them signed in because the network failed is the wrong trade: the intent was to
         * leave, and the server-side session expires on its own. The cookie is cleared by the server's
         * response when it succeeds, so a failure here can leave a live cookie — which a later
         * public-mode boot clears before it adopts the shared account (`adoptSharedAccount`).
         */
        if (!(error instanceof ApiError)) throw error;
      }
      accessToken = null;
      setState({ user: null, restored: true });
    },

    setUser(user) {
      setState({ user });
    },
  };
}
